import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi, membershipApi } from "../lib/api";
import { CheckCircle2, Loader2, Upload, Trash2, Send, Sparkles, ShieldCheck, Users, Store } from "lucide-react";

const initial = {
  first_name: "", last_name: "", company_name: "", business_name: "",
  category_slug: "", subcategory_slug: "",
  address: "", phone: "", whatsapp: "", email: "", website: "",
  instagram_url: "", facebook_url: "", tiktok_url: "", youtube_url: "", linkedin_url: "",
  short_description: "",
  logo_url: "", gallery: [], video_url: "",
  privacy_consent: false, terms_consent: false,
};

const Label = ({ children, required }) => (
  <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-2">
    {children}{required && <span className="text-[#12964A]"> *</span>}
  </div>
);

const Input = (p) => (
  <input {...p} className="w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 outline-none focus:border-[#12964A] transition-colors" />
);

const Textarea = (p) => (
  <textarea {...p} className="w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 outline-none focus:border-[#12964A]" />
);

const Select = (p) => (
  <select {...p} className="w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 outline-none focus:border-[#12964A]" />
);

const StepHeader = ({ n, title, subtitle }) => (
  <div className="mb-6">
    <div className="inline-flex items-center gap-3 mb-2">
      <span className="w-8 h-8 rounded-full bg-[#12964A] text-white text-sm font-bold flex items-center justify-center">{n}</span>
      <h2 className="font-display font-bold text-xl md:text-2xl text-[#1F2937]">{title}</h2>
    </div>
    {subtitle && <p className="text-slate-500 text-sm ml-11">{subtitle}</p>}
  </div>
);

const JoinCCN = () => {
  const [f, setF] = useState(initial);
  const [categories, setCategories] = useState([]);
  const [allSubs, setAllSubs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([publicApi.listCategories(), publicApi.listSubcategories()])
      .then(([c, s]) => { setCategories(c.data || []); setAllSubs(s.data || []); })
      .catch(() => { /* noop */ });
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const subsForCat = useMemo(() => allSubs.filter((s) => s.category?.slug === f.category_slug), [allSubs, f.category_slug]);

  const uploadOne = async (kind, file) => {
    if (!file) return;
    setUploading(kind); setError(null);
    try {
      const r = await membershipApi.upload(file, kind === "logo" ? "membership-logos" : "membership-gallery");
      const url = r.data.public_url;
      if (kind === "logo") set("logo_url", url);
      else set("gallery", [...(f.gallery || []), { url, caption: "" }]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento immagine.");
    } finally { setUploading(null); }
  };
  const uploadMultiple = async (files) => { for (const file of files) await uploadOne("gallery", file); };
  const removeGalleryItem = (i) => set("gallery", f.gallery.filter((_, k) => k !== i));

  const validate = () => {
    if (!f.first_name.trim() || !f.last_name.trim()) return "Nome e cognome sono obbligatori.";
    if (!f.business_name.trim()) return "Il nome dell'attività è obbligatorio.";
    if (!f.email.trim() || !/^\S+@\S+\.\S+$/.test(f.email)) return "Inserisci un'email valida.";
    if (!f.privacy_consent) return "Devi accettare l'informativa privacy per procedere.";
    if (!f.terms_consent) return "Devi accettare i termini di adesione per procedere.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setSubmitting(true); setError(null);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (!payload.gallery?.length) delete payload.gallery;
      const r = await membershipApi.apply(payload);
      setSuccess(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Impossibile inviare la richiesta. Riprova più tardi.");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="pt-40 pb-24 min-h-screen bg-[#F8F9FA]">
        <div className="max-w-2xl mx-auto px-6 text-center" data-testid="join-success">
          <div className="w-20 h-20 rounded-full bg-[#E8F5EC] mx-auto flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#12964A]" />
          </div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Richiesta inviata!</h1>
          <p className="text-lg text-slate-600 mt-4">
            Grazie <strong>{f.first_name}</strong>. Abbiamo ricevuto la tua richiesta per <strong>{f.business_name}</strong>. La segreteria del CCN Sanluri la valuterà e ti ricontatterà via email entro pochi giorni.
          </p>
          <p className="text-sm text-slate-500 mt-6">Codice richiesta: <code className="bg-white px-2 py-1 rounded border border-[#E5E7EB]">{success.id?.slice(0, 8)}</code></p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E5E7EB] hover:border-[#12964A]">Torna alla home</Link>
            <Link to="/attivita" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#12964A] text-white hover:bg-[#0E7C3D]">Esplora la directory</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 bg-[#F8F9FA] min-h-screen">
      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 mb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#12964A] mb-4">
              <Sparkles className="w-3.5 h-3.5" /> Fai crescere la tua attività
            </div>
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-[#1F2937] leading-[1.05]">
              Diventa socio<br />
              <span className="text-[#12964A]">del CCN Sanluri</span>
            </h1>
            <p className="text-lg text-slate-600 mt-6 max-w-xl">
              Unisci la tua attività al Centro Commerciale Naturale di Sanluri.
              Visibilità sul portale, eventi condivisi, promozioni congiunte e
              una comunità di oltre 100 imprese del centro storico.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><Store className="w-4 h-4 text-[#12964A]" /> Scheda pubblica ricca</div>
              <div className="flex items-center gap-2 text-slate-700"><Users className="w-4 h-4 text-[#12964A]" /> Eventi condivisi</div>
              <div className="flex items-center gap-2 text-slate-700"><ShieldCheck className="w-4 h-4 text-[#12964A]" /> Gestione dati riservata</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-[#F4E500] rounded-3xl rotate-[-3deg]" />
              <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80" alt="" loading="lazy" className="relative rounded-3xl w-full aspect-[4/5] object-cover shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <form onSubmit={submit} className="max-w-[1000px] mx-auto px-6 lg:px-10 space-y-6" data-testid="join-form">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-5 py-4 text-sm" data-testid="join-error">{error}</div>
        )}

        <section className="bg-white rounded-3xl border border-[#E5E7EB] p-8">
          <StepHeader n={1} title="I tuoi dati" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label><Label required>Nome</Label><Input required value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="join-first-name" /></label>
            <label><Label required>Cognome</Label><Input required value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="join-last-name" /></label>
            <label className="md:col-span-2"><Label>Ragione sociale (facoltativo)</Label><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="es. Mario Rossi S.r.l." /></label>
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-[#E5E7EB] p-8">
          <StepHeader n={2} title="La tua attività" subtitle="Come apparirà sul portale CCN Sanluri" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="md:col-span-2"><Label required>Nome attività</Label><Input required value={f.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="es. Panificio Medau" data-testid="join-business-name" /></label>
            <label><Label>Categoria</Label>
              <Select value={f.category_slug} onChange={(e) => { set("category_slug", e.target.value); set("subcategory_slug", ""); }} data-testid="join-category">
                <option value="">— Seleziona —</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </Select>
            </label>
            <label><Label>Sottocategoria</Label>
              <Select value={f.subcategory_slug} onChange={(e) => set("subcategory_slug", e.target.value)} disabled={!f.category_slug || subsForCat.length === 0} data-testid="join-subcategory">
                <option value="">{!f.category_slug ? "Seleziona prima categoria" : subsForCat.length === 0 ? "Nessuna sotto-categoria" : "— Seleziona —"}</option>
                {subsForCat.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </Select>
            </label>
            <label className="md:col-span-2"><Label>Breve descrizione</Label><Textarea rows={4} value={f.short_description} onChange={(e) => set("short_description", e.target.value)} placeholder="Racconta in poche righe cosa fai e cosa ti distingue" data-testid="join-short-description" /></label>
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-[#E5E7EB] p-8">
          <StepHeader n={3} title="Contatti" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="md:col-span-2"><Label>Indirizzo</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Via / Piazza, Numero civico, Sanluri" /></label>
            <label><Label>Telefono</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+39 070 ..." /></label>
            <label><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+39 3XX ..." /></label>
            <label><Label required>Email</Label><Input type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="tua@email.it" data-testid="join-email" /></label>
            <label><Label>Sito web</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" /></label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5 pt-5 border-t border-[#F1F5F9]">
            <label><Label>Instagram</Label><Input value={f.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/..." /></label>
            <label><Label>Facebook</Label><Input value={f.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} placeholder="https://facebook.com/..." /></label>
            <label><Label>TikTok</Label><Input value={f.tiktok_url} onChange={(e) => set("tiktok_url", e.target.value)} placeholder="https://tiktok.com/@..." /></label>
            <label><Label>YouTube</Label><Input value={f.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} placeholder="https://youtube.com/..." /></label>
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-[#E5E7EB] p-8">
          <StepHeader n={4} title="Media" subtitle="Logo, foto e (facoltativo) un video" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <Label>Logo</Label>
              {f.logo_url ? (
                <div className="relative w-40 h-40 rounded-2xl overflow-hidden border border-[#E5E7EB]">
                  <img src={f.logo_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("logo_url", "")} className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow"><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
                </div>
              ) : (
                <label className="w-40 h-40 rounded-2xl border-2 border-dashed border-[#E5E7EB] hover:border-[#12964A] flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:text-[#12964A]">
                  {uploading === "logo" ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Upload className="w-6 h-6 mb-2" /><span className="text-xs">Carica logo</span></>}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadOne("logo", e.target.files?.[0])} data-testid="join-upload-logo" />
                </label>
              )}
            </div>
            <div>
              <Label>Video (URL YouTube / Vimeo)</Label>
              <Input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://youtube.com/watch?v=..." />
            </div>
          </div>
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <Label>Galleria fotografica</Label>
              <label className="inline-flex items-center gap-2 text-sm text-[#12964A] cursor-pointer hover:underline">
                <Upload className="w-4 h-4" /> {uploading === "gallery" ? "Caricamento…" : "Aggiungi foto"}
                <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => uploadMultiple(Array.from(e.target.files || []))} data-testid="join-upload-gallery" />
              </label>
            </div>
            {f.gallery.length === 0 ? (
              <p className="text-sm text-slate-400">Nessuna foto ancora. Consigliamo 3–6 foto rappresentative.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {f.gallery.map((g, i) => (
                  <div key={g.url} className="relative aspect-square rounded-2xl overflow-hidden bg-slate-100">
                    <img src={g.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeGalleryItem(i)} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5"><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-[#E5E7EB] p-8">
          <StepHeader n={5} title="Consenso" subtitle="Passaggio obbligatorio per procedere" />
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={f.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} className="mt-1 w-5 h-5 accent-[#12964A]" data-testid="join-privacy" />
              <span className="text-sm text-slate-700">
                Ho letto e accetto l&apos;<a href="/privacy" target="_blank" className="text-[#12964A] underline">informativa privacy</a> e acconsento al trattamento dei miei dati per la gestione della richiesta di adesione al CCN Sanluri (Reg. UE 2016/679).
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={f.terms_consent} onChange={(e) => set("terms_consent", e.target.checked)} className="mt-1 w-5 h-5 accent-[#12964A]" data-testid="join-terms" />
              <span className="text-sm text-slate-700">
                Accetto i <a href="/statuto" target="_blank" className="text-[#12964A] underline">termini e lo statuto</a> del Centro Commerciale Naturale di Sanluri.
              </span>
            </label>
          </div>
        </section>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white rounded-3xl border border-[#E5E7EB] p-6">
          <p className="text-sm text-slate-500">Riceverai una email di conferma appena inviata la richiesta.</p>
          <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-50 text-white rounded-full px-8 py-3.5 font-medium transition-colors" data-testid="join-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Invio…</> : <><Send className="w-4 h-4" /> Invia richiesta di adesione</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JoinCCN;
